import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Check, EyeOff, Image as ImageIcon, Info } from 'lucide-react';

import { SoftCard } from '@/components/ui/soft-card';
import { Toggle } from '@/components/ui/toggle';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import channelDefinitions from '@nearst/channel-definitions';

import { useShop } from '@/hooks/useShop';
import { useShopPath } from '@/hooks/useShopPath';
import { useGoBack } from '@/hooks/useGoBack';
import { useStockRecord, editProduct, hideProducts, setProductChannels } from '@/hooks/useInventory';
import { saveFileToS3 } from '@services/shop';

import { CategoryPicker } from './components/CategoryPicker';
import { DeliveryChannelsCard } from './components/DeliveryChannelsCard';

function Field({ label, required, children }) {
	return (
		<div>
			<div className="mb-1.5 text-sm font-semibold text-foreground">
				{label} {required ? <span className="text-destructive">*</span> : null}
			</div>
			{children}
		</div>
	);
}

function StockRow({ label, value, mono }) {
	return (
		<div className="flex items-center justify-between py-1.5 text-sm">
			<span className="text-muted-foreground">{label}</span>
			<span className={`font-semibold text-foreground ${mono ? 'tabular-nums' : ''}`}>{value}</span>
		</div>
	);
}

const INPUT_CLASS =
	'w-full h-11 px-3.5 rounded-lg border border-border bg-white text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20';

// Per-channel visibility tiers, mirroring the backend visibility gate at
// interfaces/library/api-handlers/src/handlers/inventory/lib/visibility-gate.js.
// We resolve `issuesChannelId` here too so a channel that shares another's
// issues profile (e.g. Local Ads → Google) inherits the same tier without
// needing to be re-listed below.
const SELF_SOURCING_CHANNELS = new Set(['google', 'local-ads']);
const TITLE_FLOOR_CHANNELS = new Set(['storefront']);
const resolveIssuesChannelId = (id) => channelDefinitions[id]?.issuesChannelId ?? id;

// One of 'visible' | 'blocked' | 'hidden'. Retailer-hidden and product-hidden
// both collapse to 'hidden'; Self-sourcing channels always render 'visible' if
// enabled; Storefront's 'blocked' key is the title issue alone; every other
// channel blocks on any issue.
function computeChannelVisibility(channelId, on, record) {
	if (!record) return 'hidden';
	if (record.hidden || !on) return 'hidden';
	const id = resolveIssuesChannelId(channelId);
	if (SELF_SOURCING_CHANNELS.has(id)) return 'visible';
	const issues = record.issues || [];
	if (TITLE_FLOOR_CHANNELS.has(id)) {
		return issues.includes('MissingProductTitle') ? 'blocked' : 'visible';
	}
	return issues.length > 0 ? 'blocked' : 'visible';
}

// Per-issue content for the Fix modal. Each entry contributes one stacked card
// (attribute name + hint + primary action) inside the modal. Issues that don't
// appear here (e.g. MissingDeliveryCategories, InvalidCondition) aren't fixable
// from this page — they're filtered out, and if a channel has none of these
// fixable issues the Fix pill is suppressed entirely.
const FIX_MODAL_CONTENT = {
	MissingProductImages: {
		attribute: 'Product image',
		hint: 'Upload a high-quality image (min. 800×800px, white or plain background, no watermarks).',
		actionLabel: 'Upload image',
		field: 'image'
	},
	MissingProductTitle: {
		attribute: 'Product title',
		hint: 'Add a descriptive title so shoppers can find and identify your product.',
		actionLabel: 'Add title',
		field: 'title'
	},
	MissingProductBrand: {
		attribute: 'Product brand',
		hint: "Add the manufacturer or maker's brand name (e.g. Command, Nike).",
		actionLabel: 'Add brand',
		field: 'brand'
	},
	MissingProductDescription: {
		attribute: 'Product description',
		hint: 'Write a short description explaining what the product is.',
		actionLabel: 'Add description',
		field: 'description'
	},
	MissingProductCategory: {
		attribute: 'Product category',
		hint: 'Pick the category that best describes this product.',
		actionLabel: 'Pick category',
		field: 'category'
	}
};

// Given a channel and the record's issues, return every issue type that blocks
// that channel AND has a Fix modal entry. Self-sourcing channels never block;
// storefront blocks only on missing title; others block on any issue.
function findFixableIssues(channelId, record) {
	if (!record) return [];
	const id = resolveIssuesChannelId(channelId);
	if (SELF_SOURCING_CHANNELS.has(id)) return [];
	const issues = record.issues || [];
	if (TITLE_FLOOR_CHANNELS.has(id)) {
		return issues.includes('MissingProductTitle') ? ['MissingProductTitle'] : [];
	}
	return issues.filter((i) => FIX_MODAL_CONTENT[i]);
}

export function ProductDetail() {
	const { barcode = '' } = useParams();
	const { shop } = useShop();
	const { shopPath } = useShopPath();
	const goBack = useGoBack('/inventory/manage');
	const { record, isLoading, mutate } = useStockRecord(shop?.id, barcode);

	// The shop resolves asynchronously; until its id is known the record query
	// can't even start (the SWR key is null, so `isLoading` stays false).
	// Treat that window as loading too, otherwise the "not found" card flashes
	// for a product that actually exists.
	const resolvingRecord = !shop?.id || isLoading;

	const [form, setForm] = useState({ title: '', brand: '', description: '', category: null });
	const [imageSrc, setImageSrc] = useState('');
	const [imageFile, setImageFile] = useState(null);
	const [saving, setSaving] = useState(false);
	const [saveError, setSaveError] = useState(null);
	const [togglingActive, setTogglingActive] = useState(false);
	const [channelsOpen, setChannelsOpen] = useState(false);
	const [channelDraft, setChannelDraft] = useState({});
	const [savingChannels, setSavingChannels] = useState(false);
	// Fix modal state — { channelId } while open, null while closed. Set by the
	// Fix pill on a blocked channel row; the modal then lists every fixable
	// issue affecting that channel. Cleared by Cancel or by a per-issue action.
	const [fixModal, setFixModal] = useState(null);

	// Refs for the form controls that the Fix modal's primary action jumps to.
	// The image ref is the hidden file input (we call .click() to open the
	// picker); the rest are focused + scrolled into view.
	const titleRef = useRef(null);
	const brandRef = useRef(null);
	const descriptionRef = useRef(null);
	const categoryRef = useRef(null);
	const imageInputRef = useRef(null);

	const applyFix = (field) => {
		setFixModal(null);
		if (!field) return;
		const refs = { title: titleRef, brand: brandRef, description: descriptionRef, category: categoryRef, image: imageInputRef };
		const node = refs[field]?.current;
		if (!node) return;
		if (field === 'image') {
			node.click();
			return;
		}
		node.scrollIntoView({ behavior: 'smooth', block: 'center' });
		// Focus after the scroll starts so the caret jump doesn't fight the scroll.
		setTimeout(() => node.focus?.(), 100);
	};

	// Channels editable per-product: those the shop has configured for this
	// item (a key on `record.channels`) AND that support product-specific
	// visibility — same shape the retailers dashboard's
	// EditChannelVisibilityModal uses.
	const channelLabel = (id) => shop?.availableChannels?.[id]?.label ?? id.replace(/-/g, ' ');
	const editableChannels = Object.keys(record?.channels ?? {})
		.filter((id) => shop?.availableChannels?.[id]?.productSpecificVisibility)
		.map((id) => ({ id, label: channelLabel(id) }));

	useEffect(() => {
		if (!record) return;
		// productCategory can arrive as a string (legacy "Pet Food") or an
		// object ({ id, title, full_tree }). Normalise into the picker's shape.
		const initialCategory = record.productCategory
			? typeof record.productCategory === 'object'
				? { id: record.productCategory.id, title: record.productCategory.title, fullTree: record.productCategory.full_tree }
				: { id: null, title: record.productCategory, fullTree: null }
			: null;
		setForm({
			title: record.title ?? '',
			brand: record.brand ?? '',
			description: record.description ?? '',
			category: initialCategory
		});
		setImageSrc(record.images?.[0] ?? '');
		setImageFile(null);
	}, [record]);

	const setField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

	const onPickImage = (e) => {
		const file = e.target.files?.[0];
		if (!file?.name) return;
		setImageFile(file);
		setImageSrc(URL.createObjectURL(file));
	};

	const onSave = async () => {
		if (!shop?.id) return;
		setSaving(true);
		setSaveError(null);
		try {
			// Only a freshly-picked file needs uploading; an unchanged image
			// keeps the record's existing URL.
			let images = record?.images ?? [];
			if (imageFile) {
				const url = await saveFileToS3(shop.id, imageFile);
				images = [url, ...images.filter((u) => u !== url)];
			}
			await editProduct(shop.id, barcode, {
				title: form.title,
				brand: form.brand,
				description: form.description,
				images,
				// Sending categoryIds matches the retailers-dashboard write path.
				// Empty array clears the override. A picked-but-no-id entry (legacy
				// string-only data) is sent as the `category` title instead.
				...(form.category?.id ? { categoryIds: [form.category.id] } : {}),
				...(form.category && !form.category.id ? { category: form.category.title } : {}),
				...(form.category === null ? { categoryIds: [], category: '' } : {})
			});
			await mutate();
			setImageFile(null);
			// Return to the product overview so the save is visibly acknowledged
			// instead of silently staying put (NEA-5977).
			goBack();
		} catch (e) {
			setSaveError(e?.response?.data?.message ?? e.message ?? 'Something went wrong saving this product.');
		} finally {
			setSaving(false);
		}
	};

	const onToggleActive = async () => {
		if (!shop?.id || !record) return;
		setTogglingActive(true);
		try {
			await hideProducts(shop.id, { barcodes: [barcode], enabled: record.hidden });
			await mutate();
		} finally {
			setTogglingActive(false);
		}
	};

	// Every channel configured on this item, alphabetised — the tri-state
	// icon carries the affordance so we don't need to hoist blocked/hidden
	// rows out of alphabetical order (matches the design).
	const productHidden = !!record?.hidden;
	const channelStates = record?.channels
		? Object.entries(record.channels)
				.map(([id, on]) => ({ id, on: !!on, state: computeChannelVisibility(id, !!on, record) }))
				.sort((a, b) => channelLabel(a.id).localeCompare(channelLabel(b.id)))
		: [];
	const visibleChannelCount = channelStates.filter((c) => c.state === 'visible').length;

	const openChannelEditor = () => {
		const current = record?.channels ?? {};
		setChannelDraft(Object.fromEntries(editableChannels.map((c) => [c.id, current[c.id] !== false])));
		setChannelsOpen(true);
	};

	const saveChannels = async () => {
		if (!shop?.id || !record) return;
		setSavingChannels(true);
		try {
			await setProductChannels(shop.id, [barcode], { ...(record.channels ?? {}), ...channelDraft });
			await mutate();
			setChannelsOpen(false);
		} finally {
			setSavingChannels(false);
		}
	};

	return (
		<div className="space-y-6">
			<button
				type="button"
				onClick={goBack}
				className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted-foreground transition hover:text-foreground"
			>
				<ArrowLeft className="h-3.5 w-3.5" /> Back
			</button>

			<header>
				<h1 className="text-3xl font-extrabold tracking-tight tabular-nums md:text-[32px]">{barcode}</h1>
				<p className="mt-1.5 text-muted-foreground">Edit product information and configure visibility across enabled channels.</p>
			</header>

			{resolvingRecord ? (
				<div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
					<Skeleton className="h-[460px] w-full rounded-2xl" />
					<Skeleton className="h-[320px] w-full rounded-2xl" />
				</div>
			) : !record ? (
				<SoftCard className="!p-8 text-center">
					<p className="text-sm text-muted-foreground">
						We couldn't find a product with barcode <span className="font-semibold text-foreground">{barcode}</span> in your
						catalogue.
					</p>
				</SoftCard>
			) : (
				<div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
					<SoftCard className="!p-6">
						<div className="mb-5 flex items-center justify-between">
							<h2 className="text-base font-bold text-foreground">Product data</h2>
							<TooltipProvider delayDuration={150}>
								<Tooltip>
									<TooltipTrigger asChild>
										<span className="inline-flex cursor-help items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
											Third party data
											<Info className="h-3 w-3" />
										</span>
									</TooltipTrigger>
									<TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">
										This product information is sourced from publicly available data. We recommend reviewing it for
										accuracy and refining any details to best represent your product.
									</TooltipContent>
								</Tooltip>
							</TooltipProvider>
						</div>

						<div className="grid grid-cols-1 gap-5 md:grid-cols-[1fr_180px]">
							<div className="space-y-4">
								<Field label="Title" required>
									<input
										ref={titleRef}
										type="text"
										value={form.title}
										onChange={setField('title')}
										className={INPUT_CLASS}
									/>
								</Field>
								<Field label="Brand">
									<input
										ref={brandRef}
										type="text"
										value={form.brand}
										onChange={setField('brand')}
										className={INPUT_CLASS}
									/>
								</Field>
								<Field label="Product category">
									<div ref={categoryRef}>
										<CategoryPicker value={form.category} onChange={(c) => setForm((f) => ({ ...f, category: c }))} />
									</div>
								</Field>
							</div>

							<Field label="Image">
								<label className="block cursor-pointer">
									<div className="flex h-[180px] items-center justify-center overflow-hidden rounded-lg border border-border bg-white p-2 transition hover:border-primary">
										{imageSrc ? (
											<img src={imageSrc} alt={form.title} className="max-h-full max-w-full object-contain" />
										) : (
											<ImageIcon className="h-10 w-10 text-muted-foreground" />
										)}
									</div>
									<span className="mt-2 block w-full text-center text-sm font-semibold text-primary hover:underline">
										{imageSrc ? 'Edit image' : 'Upload image'}
									</span>
									<input ref={imageInputRef} type="file" accept="image/*" onChange={onPickImage} className="hidden" />
								</label>
							</Field>
						</div>

						<div className="mt-5">
							<Field label="Description">
								<textarea
									ref={descriptionRef}
									rows={6}
									value={form.description}
									onChange={setField('description')}
									className="w-full resize-y rounded-lg border border-border bg-white px-3.5 py-2.5 text-sm leading-relaxed outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
								/>
							</Field>
						</div>

						<div className="mt-5 flex items-center gap-3">
							<button
								type="button"
								onClick={onSave}
								disabled={saving}
								className="inline-flex items-center rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90 disabled:opacity-50"
							>
								{saving ? 'Saving…' : 'Save'}
							</button>
							<Link
								to={shopPath('/inventory/manage')}
								className="inline-flex items-center rounded-full border border-border bg-white px-5 py-2 text-sm font-semibold text-foreground transition hover:bg-accent"
							>
								Cancel
							</Link>
						</div>
						{saveError ? <p className="mt-3 text-sm text-destructive">{saveError}</p> : null}
					</SoftCard>

					<div className="space-y-6">
						<SoftCard className="!p-5">
							<h3 className="mb-3 text-base font-bold">Stock data</h3>
							<StockRow label="Barcode" value={barcode} mono />
							<StockRow label="Price" value={record.price != null ? `£${Number(record.price).toFixed(2)}` : '—'} />
							<StockRow label="Quantity" value={record.quantity != null ? `${record.quantity} in stock` : '—'} />
						</SoftCard>

						<SoftCard className="!p-5">
							<div className="mb-1 flex items-center gap-1.5">
								<h3 className="text-base font-bold">Visibility</h3>
								<TooltipProvider delayDuration={150}>
									<Tooltip>
										<TooltipTrigger asChild>
											<a
												href="https://unify.helpkit.so/inventory/7JASpDe6Fw24Gk9K36f1oP/how-to-hide-a-product/7MhDo5Gbmb4GXWaGEdRKvj"
												target="_blank"
												rel="noopener noreferrer"
												aria-label="How to hide a product"
												className="inline-flex items-center rounded-full text-muted-foreground transition hover:text-foreground"
											>
												<Info className="h-3.5 w-3.5" />
											</a>
										</TooltipTrigger>
										<TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">
											You can hide/unhide this product across all channels by switching on/off the toggle. Or manage
											visibility by clicking 'Edit channel visibility' below.
										</TooltipContent>
									</Tooltip>
								</TooltipProvider>
							</div>
							<p className="mb-3 text-sm text-muted-foreground">
								{!channelStates.length
									? 'This product is not on any channels yet.'
									: productHidden
										? 'This product is hidden from all channels.'
										: `This product is visible in ${visibleChannelCount} of ${channelStates.length} channel${channelStates.length === 1 ? '' : 's'}.`}
							</p>
							<Toggle
								checked={!record.hidden}
								onChange={onToggleActive}
								disabled={togglingActive}
								label={record.hidden ? 'Hidden' : 'Active'}
							/>

							{channelStates.length > 0 ? (
								<>
									<div className="mb-2.5 mt-5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
										Per channel
									</div>
									<ul className="space-y-2.5">
										{channelStates.map(({ id, state }) => (
											<li
												key={id}
												data-testid={`channel-row-${id}`}
												data-state={state}
												className="flex items-center justify-between gap-2.5"
											>
												<div className="flex items-center gap-2.5">
													<span
														className={
															state === 'visible'
																? 'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-success-soft text-success-soft-foreground'
																: state === 'blocked'
																	? 'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700'
																	: 'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground'
														}
													>
														{state === 'visible' ? (
															<Check className="h-3 w-3" />
														) : state === 'blocked' ? (
															<AlertTriangle className="h-3 w-3" />
														) : (
															<EyeOff className="h-3 w-3" />
														)}
													</span>
													<div
														className={`text-sm font-semibold ${
															state === 'hidden' ? 'text-muted-foreground' : 'text-foreground'
														}`}
													>
														{channelLabel(id)}
													</div>
												</div>
												{state === 'blocked' && findFixableIssues(id, record).length > 0 ? (
													<button
														type="button"
														onClick={() => setFixModal({ channelId: id })}
														className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-[11px] font-semibold text-amber-700 transition hover:bg-amber-200"
													>
														Fix
													</button>
												) : null}
											</li>
										))}
									</ul>
									{editableChannels.length > 0 ? (
										<button
											type="button"
											onClick={openChannelEditor}
											className="mt-5 inline-flex w-full items-center justify-center rounded-full bg-muted px-4 py-2 text-sm font-semibold text-foreground transition hover:bg-accent"
										>
											Edit channel visibility
										</button>
									) : null}
								</>
							) : null}
						</SoftCard>
					</div>
				</div>
			)}

			<DeliveryChannelsCard record={record} shopId={shop?.id} onSaved={mutate} />

			<Dialog open={!!fixModal} onOpenChange={(open) => !open && setFixModal(null)}>
				{/* Modal caps at 85vh and uses a flex column so the pill / header /
				    Cancel stay pinned while just the issue-cards list scrolls
				    internally — otherwise a product with all five missing fields
				    pushes Cancel below the viewport on shorter screens. */}
				<DialogContent className="flex max-h-[85vh] flex-col rounded-2xl sm:max-w-md">
					{fixModal
						? (() => {
								const issues = findFixableIssues(fixModal.channelId, record);
								if (!issues.length) return null;
								return (
									<>
										<div className="shrink-0">
											<span
												data-testid="fix-modal-eligibility"
												className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-700"
											>
												Not eligible
											</span>
										</div>
										<DialogHeader className="shrink-0">
											<DialogTitle className="text-xl font-bold">Fix product data</DialogTitle>
											<DialogDescription>
												{channelLabel(fixModal.channelId)} requires the following to show this product:
											</DialogDescription>
										</DialogHeader>
										<div className="-mx-6 min-h-0 space-y-3 overflow-y-auto px-6">
											{issues.map((issueType) => {
												const entry = FIX_MODAL_CONTENT[issueType];
												return (
													<div
														key={issueType}
														data-testid={`fix-modal-issue-${issueType}`}
														className="rounded-xl border border-border bg-muted/40 p-4"
													>
														<div className="mb-1 text-sm font-semibold text-foreground">{entry.attribute}</div>
														<p className="text-sm text-muted-foreground">{entry.hint}</p>
														<div className="mt-3 flex justify-end">
															<button
																type="button"
																onClick={() => applyFix(entry.field)}
																className="inline-flex items-center rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90"
															>
																{entry.actionLabel}
															</button>
														</div>
													</div>
												);
											})}
										</div>
										<div className="flex shrink-0 items-center justify-end">
											<button
												type="button"
												onClick={() => setFixModal(null)}
												className="inline-flex items-center rounded-full px-4 py-2 text-sm font-semibold text-foreground transition hover:bg-muted"
											>
												Cancel
											</button>
										</div>
									</>
								);
							})()
						: null}
				</DialogContent>
			</Dialog>

			<Dialog open={channelsOpen} onOpenChange={setChannelsOpen}>
				<DialogContent className="rounded-2xl sm:max-w-md">
					<DialogHeader>
						<DialogTitle className="text-xl font-bold">Edit channel visibility</DialogTitle>
						<DialogDescription>Select the channels where you want to display this product.</DialogDescription>
					</DialogHeader>
					<div className="space-y-4">
						<div className="flex flex-col gap-3">
							{editableChannels.map((c) => (
								<Checkbox
									key={c.id}
									label={c.label}
									checked={!!channelDraft[c.id]}
									onChange={(e) => setChannelDraft((d) => ({ ...d, [c.id]: e.target.checked }))}
								/>
							))}
						</div>
						<button
							type="button"
							onClick={saveChannels}
							disabled={savingChannels}
							className="inline-flex items-center justify-center rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90 disabled:opacity-50"
						>
							{savingChannels ? 'Saving…' : 'Confirm'}
						</button>
					</div>
				</DialogContent>
			</Dialog>
		</div>
	);
}
